import { FullLoginTokenExchangeProps } from '@msc-b2c/interfaces/types';
import { useMsal } from '@azure/msal-react';
import { EventType, InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useDispatch, useSelector } from 'react-redux';
import {
  bookingsWithImages as setBookingsWithImages,
  closeLoader,
  createNewSession,
  getActiveSession,
  GetAllBookingsMyMSC,
  getCountryCodeISO3,
  mscGet,
  mscRemove,
  openLoader,
  parseJwt,
  removeSessionByToken,
  RootState,
  setErrorManagerCode,
  setErrorManagerEnabled,
  TokenExchange,
  TokenUpgrade,
} from '@msc-b2c/shared-utils';
import { useEffect, useState } from 'react';
import { ErrorObject, TokenExchangeResponse } from '@msc-b2c/interfaces/interfaces';
import { getLoginRedirectConfig } from '../../utils/loginUtils';
import { hasCameFromRegisterCta } from '../../utils/registerCtaUtils';
import dayjs from 'dayjs';
import { AuthError } from '@azure/msal-common/src/error/AuthError';
import {
  setPendingBookingStatusAnalytics,
  setPendingNoBookingsAnalytics,
} from '../../utils/analyticsUtils';
import { computeBookingsImagesFromMetadata } from '../../planmycruisecontainer/imageRotation/imageRotation';
import { ErrorManagerCodeEnum } from '@msc-b2c/interfaces/enums';

declare global {
  interface Window {
    azureActionTriggered?: boolean;
  }
}

export function FullLoginTokenExchangeWrapper({
  id,
  completeProfilePage,
  myAreaHomepage,
  userNotVerifiedPage,
  userCanceledOperationPage,
  thankYouPageResetPasswordPage,
  noBookingsPage,
}: Readonly<FullLoginTokenExchangeProps>) {
  const { instance, inProgress, accounts } = useMsal();
  const dispatch = useDispatch();
  const [mulesoftJwt, setMulesoftJwt] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(
    instance?.getActiveAccount()?.idTokenClaims?.emails?.[0] ?? null
  );
  const activeSession = getActiveSession();
  const { crmId, token: activeMulesoftToken, email: activeSessionEmail } = activeSession ?? {};
  // Reuse only when arriving from the register CTA and a token exists to reuse.
  const shouldReuseExistingMulesoftSession = hasCameFromRegisterCta() && !!activeMulesoftToken;
  const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);

  useEffect(() => {
    window.azureActionTriggered = false;
    openLoader('.loader');
  }, []);

  const azureConfig = useSelector(
    (state: RootState) => state?.genericConfigs?.configs?.azureB2CConfig
  );
  const azureSession = useSelector((state: RootState) => state?.myAreaCommonConfig?.azureSession);
  const genericConfigs = useSelector((state: RootState) => state?.genericConfigs);

  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_FAILURE && event.error) {
        const errorMessage = (event.error as AuthError)?.errorMessage || '';

        if (errorMessage.includes(ErrorManagerCodeEnum.USER_CANCELED)) {
          window.location.href = userCanceledOperationPage || '';
        } else if (errorMessage.includes(ErrorManagerCodeEnum.ACCOUNT_CREATION_FAILED)) {
          closeLoader('.loader');
          dispatch(setErrorManagerCode(ErrorManagerCodeEnum.ACCOUNT_CREATION_FAILED));
          dispatch(setErrorManagerEnabled(true));
        } else {
          closeLoader('.loader');
          dispatch(setErrorManagerCode(ErrorManagerCodeEnum.GENERIC_LOGIN_ERROR));
          dispatch(setErrorManagerEnabled(true));
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  function ProtectedComponent() {
    // Reuse the register session (INT112) and skip INT116; bookings check still runs below.
    useEffect(() => {
      if (!shouldReuseExistingMulesoftSession) return;
      if (inProgress !== InteractionStatus.None) return;
      if (window.azureActionTriggered) return;

      window.azureActionTriggered = true;
      setUsername(
        activeSessionEmail ?? instance?.getActiveAccount()?.idTokenClaims?.emails?.[0] ?? username
      );
      setMulesoftJwt(activeMulesoftToken ?? null);
      setPendingBookingStatusAnalytics();
    }, [
      activeMulesoftToken,
      activeSessionEmail,
      inProgress,
      shouldReuseExistingMulesoftSession,
      username,
    ]);

    useEffect(() => {
      const isPasswordResetFlow =
        azureSession?.authority &&
        azureConfig?.resetPasswordUserFlow &&
        azureSession.authority
          .toLowerCase()
          .includes(azureConfig.resetPasswordUserFlow.toLowerCase());

      if (isPasswordResetFlow) {
        window.azureActionTriggered = true;
        instance.clearCache().then(() => {
          window.location.href = thankYouPageResetPasswordPage || '';
        });
        return;
      }

      const accessTokenRequest = {
        ...getLoginRedirectConfig(azureConfig, 'login', window.location.href),
        account: instance.getActiveAccount() ?? accounts[0],
        authority: azureSession?.authority,
      };

      setIsForgotPassword(accessTokenRequest?.account?.idTokenClaims?.isForgotPassword as boolean);

      if (
        !mulesoftJwt &&
        inProgress === InteractionStatus.None &&
        instance.getActiveAccount() &&
        !window.azureActionTriggered &&
        !shouldReuseExistingMulesoftSession
      ) {
        window.azureActionTriggered = true;
        instance
          .acquireTokenSilent(accessTokenRequest)
          .then((accessTokenResponse) => {
            // Acquire token silent success
            const { idToken } = accessTokenResponse;
            const accountUsername =
              accessTokenResponse?.account?.idTokenClaims?.emails?.[0] ?? null;
            setUsername(accountUsername);
            createNewSession(idToken, 'full', 'mymsc', accountUsername || undefined);
            setPendingBookingStatusAnalytics();

            // Call your API with token
            const tokenExchangeApi = new TokenExchange(false, false, undefined, '');

            tokenExchangeApi
              ?.sendData()
              .then((data: TokenExchangeResponse) => {
                removeSessionByToken(idToken);

                const { token, crmasisId, goldenId, loginMode, name, surname } = data;
                const dateOfBirth = (parseJwt(token) as { birthDate?: string })?.birthDate;
                createNewSession(
                  token,
                  loginMode,
                  'mymsc',
                  accountUsername || undefined,
                  name || undefined,
                  surname || undefined,
                  undefined,
                  undefined,
                  crmasisId,
                  goldenId,
                  undefined,
                  undefined,
                  dateOfBirth
                );

                if (crmasisId) {
                  setMulesoftJwt(token);
                } else {
                  console.log('User profile not found - redirecting to complete profile');
                  window.location.href = completeProfilePage || '';
                }
              })
              .catch((error: ErrorObject) => {
                closeLoader('.loader');
                if (
                  error.code === 403 &&
                  error.issue_code === ErrorManagerCodeEnum.CUSTOMER_PROFILE_INCOMPLETE
                ) {
                  window.location.href = userNotVerifiedPage;
                } else if (
                  error.code === 403 &&
                  error.issue_code === ErrorManagerCodeEnum.ACCESS_DENIED
                ) {
                  dispatch(setErrorManagerCode(ErrorManagerCodeEnum.LOGIN_ACCESS_DENIED));
                } else {
                  dispatch(
                    setErrorManagerCode(
                      error.issue_code || ErrorManagerCodeEnum.GENERIC_LOGIN_ERROR
                    )
                  );
                }
              });
          })
          .catch((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
            console.log(error);
          });
      }
    }, [azureSession]);

    useEffect(() => {
      if (mulesoftJwt && username) {
        if (isForgotPassword) {
          console.log(
            'User is in forgot password flow - redirecting to reset password thank you page'
          );
          window.location.href = thankYouPageResetPasswordPage || '';
        } else if (crmId) {
          const getAllMyBookings = new GetAllBookingsMyMSC(false, { email: username });

          getAllMyBookings
            .getData()
            .then((data) => {
              const bookingsOfTheMarket = data.bookings?.filter(
                (booking) => booking.marketCode === getCountryCodeISO3()
              );

              if (bookingsOfTheMarket?.length) {
                const sortedByCruiseDate = bookingsOfTheMarket?.sort(
                  (a, b) =>
                    dayjs(a.cruiseId.substring(2, 10), 'YYYYMMDD').valueOf() -
                    dayjs(b.cruiseId.substring(2, 10), 'YYYYMMDD').valueOf()
                );
                const earlyDepartureCruise = sortedByCruiseDate[0];

                // Compute the deterministic, metadata-only image rotation now,
                // before redirecting to the Overview. Persisting the result
                // (handled by the slice reducer) lets Overview reuse the same
                // rotation that All My Cruises will display, without firing a
                // /bookings or per-booking /itinerary call after login.
                dispatch(
                  setBookingsWithImages(
                    computeBookingsImagesFromMetadata(bookingsOfTheMarket, genericConfigs)
                  )
                );

                const tokenUpgrade = new TokenUpgrade(
                  false,
                  false,
                  { bookingNumber: earlyDepartureCruise.number },
                  ''
                );
                const allBookingsIds = bookingsOfTheMarket.map((booking) => booking.cruiseId);

                tokenUpgrade
                  .sendData()
                  .then((data) => {
                    removeSessionByToken(mulesoftJwt);

                    const { token, crmasisId, goldenId, loginMode, passengerId, name, surname } =
                      data;
                    const dateOfBirth = (parseJwt(token) as { birthDate?: string })?.birthDate;
                    createNewSession(
                      token,
                      loginMode,
                      'mymsc',
                      username,
                      name,
                      surname,
                      earlyDepartureCruise.number,
                      earlyDepartureCruise.cruiseId,
                      crmasisId,
                      goldenId,
                      passengerId,
                      allBookingsIds,
                      dateOfBirth
                    );
                    setPendingBookingStatusAnalytics();
                    const jwtReturnUrl = mscGet<string>('session', 'mscJwtExpiredReturnUrl');
                    if (jwtReturnUrl) {
                      mscRemove('session', 'mscJwtExpiredReturnUrl');
                      window.location.href = jwtReturnUrl;
                    } else {
                      window.location.href = myAreaHomepage || '';
                    }
                  })
                  .catch((error: ErrorObject) => {
                    closeLoader('.loader');
                    if (
                      error.code === 403 &&
                      error.issue_code === ErrorManagerCodeEnum.ACCESS_DENIED
                    ) {
                      dispatch(setErrorManagerCode(ErrorManagerCodeEnum.LOGIN_ACCESS_DENIED));
                    } else {
                      dispatch(
                        setErrorManagerCode(
                          error.issue_code || ErrorManagerCodeEnum.GENERIC_LOGIN_ERROR
                        )
                      );
                    }
                  });
              } else {
                console.log('No bookings found - redirecting to configured no-bookings page');
                setPendingBookingStatusAnalytics();
                setPendingNoBookingsAnalytics(noBookingsPage);
                window.location.href = noBookingsPage || '';
              }
            })
            .catch((error: ErrorObject) => {
              closeLoader('.loader');
              dispatch(
                setErrorManagerCode(error.issue_code || ErrorManagerCodeEnum.GENERIC_LOGIN_ERROR)
              );
            });
        } else {
          console.log('User profile not found - redirecting to complete profile');
          window.location.href = completeProfilePage || '';
        }
      }
    }, [mulesoftJwt]);

    return null;
  }

  return (
    <div
      id={id}
      className={'cmp-fulllogintokenexchange-wrapper'}
    >
      <ProtectedComponent />
    </div>
  );
}

export default FullLoginTokenExchangeWrapper;

FullLoginTokenExchangeWrapper.aemResourceType =
  'b2c-website-foundation/components/mymsc/form/fulllogintokenexchange';
